import styled, { css } from 'styled-components';
import theme from '../typography/theme';

export const spacingReset = `
  margin: 0;
  padding: 0;
  padding-inline-end: 0;
  padding-inline-start: 0;
`;

export const inlineIcon = `
  > svg.seedling-birch-icon {
    height: 1em;
    width: 1em;

    top: 0.125em;
    position: relative;
  }
`;

export const strikethrough = (props) => {
  if (props.strikethrough) {
    return 'text-decoration: line-through;';
  }
};

export const BodyStatus = {
  DEFAULT: 'default',
  ACTIVE: 'active',
};

export type BodyStatusType = $Values<typeof BodyStatus>;

export const bodyStyles = css`
  font-family: ${theme.fonts.sans};
  font-weight: ${({ medium }) =>
    medium ? theme.fontWeights.medium : theme.fontWeights.normal};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${({ status }) =>
    (status || 'default') === BodyStatus.ACTIVE
      ? theme.colors.active
      : theme.colors.primary};
  ${spacingReset}
  ${inlineIcon}
  ${strikethrough}
`;

const linkIconColorAndHeight = (color) => `
  > svg.seedling-birch-icon {
    top: .175rem;
    path {
      fill: ${color};
    }
  }
`;

export const StyledAnchorTag = styled.a`
  &&& {
    font-family: ${theme.fonts.sans};
    font-weight: ${theme.fontWeights.normal};
    font-size: 1rem;
    line-height: 1.5rem;
    text-decoration: underline;
    cursor: pointer;
    ${inlineIcon}

    ${({ danger }) => {
      if (danger) {
        return `
      color: ${theme.colors.dangerLink};
      ${linkIconColorAndHeight(theme.colors.dangerLink)}

      &:hover, &:focus {
        color: ${theme.colors.dangerLinkHover};
        ${linkIconColorAndHeight(theme.colors.dangerLinkHover)}
      }
      `;
      } else {
        return `
      color: ${theme.colors.link};
      ${linkIconColorAndHeight(theme.colors.link)}
      &:hover, &:focus {
        color: ${theme.colors.linkHover};
        ${linkIconColorAndHeight(theme.colors.linkHover)}
      }
      `;
      }
    }}
  }
`;

export const Eyebrow = styled.span`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.colors.primary};
  ${spacingReset}
  ${inlineIcon}
`;
Eyebrow.displayName = 'Eyebrow';

export const ChipText = styled.span`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.colors.primary};
  ${spacingReset}
  ${inlineIcon}
`;
ChipText.displayName = 'ChipText';

export const DangerChipText = styled(ChipText)`
  color: ${theme.colors.dangerChip};
`;
DangerChipText.displayName = 'DangerChipText';

export const UnavailableChipText = styled(ChipText)`
  text-decoration: line-through;
`;
UnavailableChipText.displayName = 'UnavailableChipText';

export const TagText = styled.span`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: ${theme.colors.primary};
  ${spacingReset}
  ${inlineIcon}
  vertical-align: text-bottom;
`;
TagText.displayName = 'TagText';

export const Legend = styled.legend`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.medium};
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: ${theme.colors.formLabel};
  ${spacingReset}
  ${inlineIcon}
`;

export const InputOption = styled.label`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${theme.colors.primary};
  ${spacingReset}
  ${inlineIcon}
`;

const StyledLabel = styled.label`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.medium};
  font-size: 0.8125rem;
  line-height: 1.125rem;
  ${spacingReset}
  ${inlineIcon}
`;

export const Label = styled(StyledLabel)`
  color: ${theme.colors.formLabel};
`;
export const RequiredText = styled(StyledLabel)`
  color: ${theme.colors.required};
`;
export const ErrorLabel = styled(StyledLabel)`
  color: ${theme.colors.error};
`;
export const ActiveLabel = styled(StyledLabel)`
  color: ${theme.colors.active};
`;

export const HelperText = styled.span`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.colors.helper};
`;
export const DangerHelperText = styled(HelperText)`
  color: ${theme.colors.dangerHelper};
`;

export const TabText = styled.span`
  ${bodyStyles}
  ${({ isSelected }) =>
    isSelected &&
    css`
      color: ${theme.colors.active};
    `}
`;

export const SecondaryNavText = styled.span`
  ${bodyStyles};
  color: ${({ isSelected }) =>
    isSelected ? theme.colors.primary : theme.colors.secondary};
`;

export const ListViewHeader = styled.p`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.medium};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.colors.listViewHeader};
  ${spacingReset}
  ${inlineIcon}
`;

export const ListViewPrimaryCell = styled.p`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.medium};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${theme.colors.listViewPrimaryCell};
  ${spacingReset}
  ${inlineIcon}

  &:hover, &:focus {
    text-decoration: underline;
  }
`;

export const ListViewSecondaryCell = styled.p`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${theme.colors.listViewSecondaryCell};
  ${spacingReset}
  ${inlineIcon}
`;

export const ListViewSecondaryInformation = styled.p`
  font-family: ${theme.fonts.sans};
  font-weight: ${theme.fontWeights.normal};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.colors.listViewSecondaryInformation};
  ${spacingReset}
  ${inlineIcon}
`;
